import type { ChatAccess, PlayerId, RoomPlayerInfo } from '@pixelworld/shared';
import { useState } from 'react';
import { da } from '../../i18n/da';
import { mutedPlayers } from '../chat/muted-players';
import {
  BefriendButton,
  FriendsElsewhere,
  type RoomFriendsList,
} from '../friends/RoomFriendControls';
import styles from './PlayerList.module.css';

interface PlayerListProps {
  players: RoomPlayerInfo[];
  selfId: PlayerId;
  /** Only in public rooms, where the host decides who writes freely (ADR 0011). */
  chatAccess: ChatAccess | null;
  onRevoke: (playerId: PlayerId) => void;
  /** Tells an adult (ADR 0018). Resolves `true` when the report arrived. */
  onReport: (playerId: PlayerId) => Promise<boolean>;
  /** Friends (ADR 0019): "Bliv venner" here, and "Kom og spil" for friends elsewhere. */
  friends: RoomFriendsList;
}

type ReportStep = { playerId: PlayerId; step: 'ask' | 'sending' | 'sent' | 'failed' };

/** The player count, which opens a list of who is in the room. */
export function PlayerList({
  players,
  selfId,
  chatAccess,
  onRevoke,
  onReport,
  friends,
}: PlayerListProps) {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<ReportStep | null>(null);
  const isHost = chatAccess?.hostId === selfId;

  const sendReport = async (playerId: PlayerId) => {
    setReport({ playerId, step: 'sending' });
    // The child should not have to read them while an adult looks at it.
    mutedPlayers.mute(playerId);
    const arrived = await onReport(playerId);
    setReport({ playerId, step: arrived ? 'sent' : 'failed' });
  };

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
          setReport(null);
        }}
      >
        <span className={styles.dot} aria-hidden="true" />
        {/* A phone shows only the number; the words stay for screen readers. */}
        <span className={styles.count} aria-hidden="true">
          {players.length}
        </span>
        <span className={styles.label} data-testid="player-count">
          {da.play.players(players.length)}
        </span>
      </button>
      {open && (
        <ul className={styles.list} aria-label={da.play.playerList}>
          {players.map((player) => {
            const host = chatAccess?.hostId === player.id;
            const allowed = chatAccess?.allowed.includes(player.id) ?? false;
            const reporting = report?.playerId === player.id ? report.step : null;
            return (
              <li key={player.id} className={styles.player}>
                <span className={styles.name}>
                  {host && <span aria-hidden="true">👑 </span>}
                  {player.displayName}
                </span>
                {host && <span className={styles.tag}>{da.play.host}</span>}
                {allowed && <span className={styles.tag}>{da.play.mayWrite}</span>}
                {isHost && allowed && (
                  <button
                    type="button"
                    className={styles.revoke}
                    onClick={() => onRevoke(player.id)}
                  >
                    {da.play.revoke}
                  </button>
                )}
                {player.id !== selfId && !reporting && (
                  <BefriendButton player={player} list={friends} />
                )}
                {player.id !== selfId && !reporting && (
                  <button
                    type="button"
                    className={styles.report}
                    onClick={() => setReport({ playerId: player.id, step: 'ask' })}
                  >
                    {da.report.button}
                  </button>
                )}
                {reporting === 'ask' && (
                  <div className={styles.reportBox} role="group">
                    <p>{da.report.ask(player.displayName)}</p>
                    <div className={styles.reportButtons}>
                      <button
                        type="button"
                        className={styles.report}
                        onClick={() => void sendReport(player.id)}
                      >
                        {da.report.confirm}
                      </button>
                      <button
                        type="button"
                        className={styles.revoke}
                        onClick={() => setReport(null)}
                      >
                        {da.report.cancel}
                      </button>
                    </div>
                  </div>
                )}
                {(reporting === 'sent' || reporting === 'failed' || reporting === 'sending') && (
                  <p className={styles.reportBox} role="status">
                    {reporting === 'sending'
                      ? da.report.sending
                      : reporting === 'sent'
                        ? da.report.sent(player.displayName)
                        : da.report.failed}
                  </p>
                )}
              </li>
            );
          })}
          <FriendsElsewhere players={players} list={friends} />
        </ul>
      )}
    </div>
  );
}
